// ════════════════════════════════════════════════════════════════════
// i18n CATALOG — the integration feed screens (namespace: feed)
//
// Every user-facing string of pages/AdminFeed.js, components/FeedMapping.js,
// components/FeedConflicts.js, components/FeedDialogs.js, components/FeedImport.js
// and the feed nav entry, in English and Modern Standard Arabic. The API answers with codes,
// never sentences (docs/integrations/feed-api-contract.md section 3), so each
// error, warning and status code has a key here; lib/feedUi.js maps a code to
// its key and falls back to a generic key for a code it doesn't know.
//
// t() does no interpolation: dynamic values (names, counts, header names)
// sit next to a prefix or suffix key and stay separate React nodes. The one
// exception to "codes, never sentences" is the history import's warnings: the
// .NET host sends them as English sentences with counts and dates in them,
// so the screen shows them as data under a translated heading, never through
// t() (docs/integrations/FEED_ALIGN_STATUS.md section 1, item 18).
// Provider and platform names (UrbanPiper, Deliveroo, ...) are proper nouns
// and stay as data, not catalog entries.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── Navigation ───────────────────────────────────────────────────────
  "nav.admin.feed": "Integrations",
  "feed.accounts.setUp": "Set up feed",

  // ── Page chrome ──────────────────────────────────────────────────────
  "feed.title": "Integrations",
  "feed.desc": "Set up order feeds for pilot accounts: create a connection, give UrbanPiper staff the webhook details, map stores and platforms, and review the typed days the feed replaces.",
  "feed.refresh": "Refresh",
  "feed.new": "New connection",
  "feed.retry": "Try again",
  "feed.close": "Close",
  "feed.cancel": "Cancel",
  "feed.dismiss": "Dismiss",
  "feed.working": "Working…",
  "feed.saving": "Saving…",

  // ── Connections list ─────────────────────────────────────────────────
  "feed.list.search": "Search account or Biz ID",
  "feed.list.empty": "No connections yet. Use New connection, or Set up feed on the Accounts page, to start a pilot.",
  "feed.list.noMatch": "No connections match this search.",
  "feed.list.loadError": "Couldn't load connections.",
  "feed.list.unmapped": "Unmapped: ",
  "feed.list.liveMonth": "Live this month: ",
  "feed.list.noEvents": "No events yet",
  "feed.list.noWarnings": "None",
  "feed.list.pauseReason": "Reason: ",
  "feed.col.account": "Account",
  "feed.col.provider": "Provider",
  "feed.col.bizId": "Biz ID",
  "feed.col.status": "Status",
  "feed.col.approval": "Billing approval",
  "feed.col.lastEvent": "Last event",
  "feed.col.stores": "Stores",
  "feed.col.ordersToday": "Orders today",
  "feed.col.warnings": "Warnings",
  "feed.action.manage": "Manage",
  "feed.action.hide": "Hide",
  "feed.action.pause": "Pause",
  "feed.action.resume": "Resume",
  "feed.action.approve": "Approve",
  "feed.action.revoke": "Revoke approval",
  "feed.action.rotate": "Rotate token",

  // ── Statuses ─────────────────────────────────────────────────────────
  "feed.status.pending": "Waiting for first order",
  "feed.status.live": "Live",
  "feed.status.paused": "Paused",
  "feed.status.disconnected": "Disconnected",
  "feed.status.unknown": "Unknown",
  "feed.approval.on": "Approved",
  "feed.approval.off": "Not approved",

  // ── Warning codes (contract 4.2) ─────────────────────────────────────
  "feed.warn.unmapped_store": "Store not mapped",
  "feed.warn.unmapped_platform": "Platform not mapped",
  "feed.warn.time_zone_missing": "Location has no time zone",
  "feed.warn.not_approved": "Not approved for billing",
  "feed.warn.access_lost": "Account lost paid access",
  "feed.warn.apply_errors": "Some events failed to apply",
  "feed.warn.fully_discounted_orders": "Fully discounted orders",
  "feed.warn.no_status_updates": "No status updates (is the second webhook missing?)",
  "feed.warn.open_orders": "Orders still not final",
  "feed.warn.quiet_store": "Store quiet in trading hours",
  "feed.warn.cofund_on_fed_channel": "Platform contribution on fed days",
  "feed.warn.unknown": "Other warning",

  // ── Error codes (contract sections 3, 5 and 7) ──────────────────────
  "feed.err.invalid_request": "The request was refused as invalid. Check the values and try again.",
  "feed.err.unauthorized": "Your session has expired. Sign in again.",
  "feed.err.forbidden": "You don't have permission to do this.",
  "feed.err.feed_not_enabled": "The feed is switched off for this provider on this server.",
  "feed.err.not_paid": "This account doesn't have paid access, so its feed can't be set up or changed.",
  "feed.err.not_found": "Not found. It may have been removed, or the feed is switched off.",
  "feed.err.connection_disconnected": "This connection is disconnected.",
  "feed.err.payload_too_large": "Too much data in one request.",
  "feed.err.rate_limited": "Too many requests. Wait a moment and try again.",
  "feed.err.internal_error": "The integration service hit an error. Try again, and report it if it keeps happening.",
  "feed.err.unavailable": "The integration service can't reach the database or the sign in service right now. Try again shortly.",
  "feed.err.unsupported_provider": "This provider isn't supported yet.",
  "feed.err.invalid_biz_id": "The Biz ID must be 1 to 20 digits.",
  "feed.err.consent_required": "Record the merchant's consent and the terms version first.",
  "feed.err.connection_exists": "This account already has an active connection for this Biz ID.",
  "feed.err.biz_id_claimed": "This Biz ID is already connected to another Netrev account.",
  "feed.err.unknown_store": "A store in this save doesn't belong to this connection. Refresh and try again.",
  "feed.err.workspace_not_in_account": "That location isn't in this connection's account.",
  "feed.err.workspace_archived": "That location is archived. Pick an active location.",
  "feed.err.channel_not_in_workspace": "That channel doesn't belong to the chosen location.",
  "feed.err.invalid_time_zone": "Unknown time zone. Use an IANA name such as Europe/London.",
  "feed.err.invalid_cutoff": "The trading day cutoff must be before 12:00.",
  "feed.err.invalid_platform_code": "Invalid platform code.",
  "feed.err.time_zone_required": "Set the location's time zone before choosing a feed start date.",
  "feed.err.channel_already_fed": "That channel is already fed by another connection.",
  "feed.err.channel_has_other_code": "This connection already maps that channel under another platform code.",
  "feed.err.channel_has_fed_days": "This route already has fed days, so it can't be removed.",
  "feed.err.feed_from_after_fed_days": "The start date can't move past days the feed already wrote.",
  "feed.err.workspace_is_fed": "The time zone and cutoff can't change while a platform at this location is live.",
  "feed.err.invalid_range": "The date range is invalid.",
  "feed.err.range_too_long": "Pick a range of 92 days or less.",
  "feed.err.too_many_choices": "Save at most 366 days at a time.",
  "feed.err.day_not_fed": "One of these days is no longer inside the feed's window. Refresh and try again.",
  "feed.err.too_many_rows": "Too many rows in one upload chunk.",
  "feed.err.invalid_status": "The connection's status doesn't allow this right now. Refresh to see its current status.",
  "feed.err.invalid_month": "Invalid month.",
  "feed.err.not_approved": "This connection isn't approved for billing yet, so it can't take an import. Once a super admin approves it, upload the file again.",
  "feed.err.connection_paused": "This connection is paused, so it can't take an import. Resume it, then upload the file again.",
  "feed.err.unreadable_report": "The integration service couldn't read this report. Download it again and upload it as it was downloaded.",
  "feed.err.preview_stale": "The report or the feed's orders changed while it was being checked. Upload the file again.",
  "feed.err.too_many_days": "This report covers too many days for one upload. Download shorter date ranges and upload them one by one.",
  "feed.err.channel_has_cofunding": "A Platform contribution entry covers days this change would hand to the feed. Co-funding stays off on fed days, so shorten or delete that entry in the Daily Log first.",
  "feed.err.auth_unavailable": "The sign in service can't be reached right now. Try again shortly.",
  "feed.err.db_unavailable": "The integration service can't reach the database right now. Try again shortly.",
  "feed.err.not_signed_in": "You're not signed in. Sign in again.",
  "feed.err.network_error": "Couldn't reach the integration service. Check your connection and try again.",
  "feed.err.timeout": "The integration service took too long to answer. Try again.",
  "feed.err.bad_response": "The integration service sent an answer this screen doesn't understand.",
  "feed.err.unexpected_response": "The integration service sent an unexpected answer.",
  "feed.err.unknown": "Something went wrong.",
  "feed.err.codePrefix": "Code: ",

  // ── Create connection dialog (plan 7.5 steps 2 to 4, shape b) ───────
  "feed.create.title": "New connection",
  "feed.create.intro": "Pilot activation: Netrev shows a webhook URL and a token once, and UrbanPiper staff add both webhooks in Atlas. Netrev holds no UrbanPiper credential.",
  "feed.create.account": "Netrev account ID",
  "feed.create.accountHint": "The full account ID. Set up feed on the Accounts page fills it in.",
  "feed.create.accountFrom": "From the Accounts list: ",
  "feed.create.accountInvalid": "Enter the full account ID (a UUID).",
  "feed.create.provider": "Provider",
  "feed.create.bizId": "UrbanPiper Biz ID",
  "feed.create.bizIdHint": "The business ID in the merchant's Atlas account. Digits only.",
  "feed.create.bizIdInvalid": "The Biz ID must be 1 to 20 digits.",
  "feed.create.checklistTitle": "Before you go on",
  "feed.create.checkPos": "The merchant already receives UrbanPiper orders in a POS or in UrbanPiper's order panel. Netrev only listens: it can't accept, reject or cancel orders.",
  "feed.create.consentTitle": "Merchant consent",
  "feed.create.consentBody": "Netrev will receive orders, discounts, cancellations and store and platform order IDs. It never stores customer names, phone numbers, addresses, rider details or free text notes. On days the feed covers, the feed's sales replace typed sales; the typed rows are kept and can be restored.",
  "feed.create.consentCheck": "The merchant has agreed to these terms. I'm recording that consent for them (for example, terms signed offline), and Netrev records me as the person who recorded it.",
  "feed.create.terms": "Terms version the merchant agreed to",
  "feed.create.termsHint": "1 to 40 characters, for example the reference on the signed data processing terms.",
  "feed.create.termsInvalid": "Enter the terms version (1 to 40 characters).",
  "feed.create.submit": "Create and show token",
  "feed.create.shownTitle": "Connection created",
  "feed.create.reconnectHint": "Reconnecting: a disconnected connection for this account and Biz ID is reused and starts over. Its old tokens are revoked, the merchant's consent is recorded again, and it needs a super admin's billing approval again.",

  // ── The one-time token (create and rotate) ──────────────────────────
  "feed.token.warning": "Copy the token now. It won't be shown again. If it's lost, rotate the token.",
  "feed.token.webhookUrl": "Webhook URL",
  "feed.token.headerName": "Header name",
  "feed.token.token": "Token",
  "feed.token.copy": "Copy",
  "feed.token.copied": "Copied",
  "feed.token.copyFallback": "Copying isn't available here. The text is selected: press Ctrl+C (or Cmd+C).",
  "feed.token.stepsTitle": "Steps for UrbanPiper staff in Atlas",
  "feed.token.step1": "Open Settings > Webhooks > Add Webhook.",
  "feed.token.step2": "Add a webhook for the event Order placed, with the webhook URL above.",
  "feed.token.step3": "Add a second webhook for the event Order status update, with the same URL.",
  "feed.token.step4": "On both webhooks, switch retry on and choose minutes.",
  "feed.token.step5Prefix": "On both webhooks, add a header named ",
  "feed.token.step5Suffix": " with the token as its value. Keep the hyphens: a header name with underscores is dropped.",
  "feed.token.share": "Send the token to UrbanPiper staff through a secure channel, never in plain email.",
  "feed.token.next": "The connection waits for its first order. Approve it for billing and map its stores and platforms before its orders count. From a channel's feed start, the feed replaces the typed sales on that channel, and the typed rows are kept for restore.",
  "feed.token.done": "I've copied it. Done",

  // ── Rotate token ─────────────────────────────────────────────────────
  "feed.rotate.title": "Rotate the token?",
  "feed.rotate.body": "A new token is shown once. The current token keeps working until the first event arrives with the new one, then it's revoked. An earlier new token that was never seen is revoked now.",
  "feed.rotate.confirm": "Rotate token",
  "feed.rotate.revokeLabel": "Also revoke the token in use now (for a token that leaked)",
  "feed.rotate.revokeHint": "Every current token stops working at once. Events UrbanPiper sends with the old token are refused (UrbanPiper retries them) until both webhooks in Atlas have the new token, so change them straight away.",
  "feed.rotate.shownTitle": "New token",
  "feed.rotate.stepPrefix": "In Atlas, change the ",
  "feed.rotate.stepSuffix": " header on both Netrev webhooks to the new token.",

  // ── Pause, resume, approval ──────────────────────────────────────────
  "feed.pause.title": "Pause this connection?",
  "feed.pause.body": "Events keep arriving and are stored, but nothing is applied to the Daily Log. Typing reopens from tomorrow; days already fed, today included, stay as they are. When you resume, sales typed meanwhile on the fed channels are replaced by the feed (the typed rows are kept for restore).",
  "feed.pause.reason": "Reason (optional)",
  "feed.pause.confirm": "Pause",
  "feed.resume.title": "Resume this connection?",
  "feed.resume.body": "Held events are applied in order. Sales typed during the pause on the fed channels are replaced by the feed (the typed rows are kept for restore), except on days kept typed.",
  "feed.resume.confirm": "Resume",
  "feed.approve.title": "Approve billing for this connection?",
  "feed.approve.body": "UrbanPiper bills Netrev per live restaurant. Once approved, events apply to the Daily Log for mapped stores and platforms.",
  "feed.approve.confirm": "Approve",
  "feed.revoke.title": "Revoke billing approval?",
  "feed.revoke.body": "Events keep arriving and are stored, but they're not applied until the connection is approved again.",
  "feed.revoke.confirm": "Revoke approval",

  // ── Notices ──────────────────────────────────────────────────────────
  "feed.notice.created": "Connection created. It waits for its first order.",
  "feed.notice.rotated": "Token rotated.",
  "feed.notice.paused": "Connection paused.",
  "feed.notice.resumed": "Connection resumed.",
  "feed.notice.approved": "Billing approved.",
  "feed.notice.revoked": "Billing approval revoked.",
  "feed.notice.mappingSaved": "Mapping saved.",
  "feed.notice.choicesSaved": "Choices saved.",
  "feed.notice.replacedDays": "Typed days replaced by the feed (rows kept for restore): ",
  "feed.notice.replaceHeld": "Days handed back to the feed with no order yet (their typed rows stay, locked, until it has one): ",
  "feed.notice.imported": "History uploaded. The feed applies it shortly.",

  // ── Connection detail ────────────────────────────────────────────────
  "feed.detail.loadError": "Couldn't load this connection.",
  "feed.detail.status": "Status",
  "feed.detail.approval": "Billing approval",
  "feed.detail.consent": "Consent",
  "feed.detail.consentNone": "Not recorded",
  "feed.detail.activation": "Activation",
  "feed.detail.activationAtlas": "UrbanPiper staff add the webhooks in Atlas",
  "feed.detail.firstEvent": "First event",
  "feed.detail.lastEvent": "Last event",
  "feed.detail.created": "Created",
  "feed.detail.paused": "Paused",
  "feed.detail.openOrders": "Orders not final yet",
  "feed.detail.unprocessed": "Events not processed yet",
  "feed.detail.ordersToday": "Orders today",
  "feed.detail.fullyDiscounted": "Fully discounted orders",
  "feed.detail.fullyDiscountedCount": "Fully discounted orders: ",
  "feed.detail.fullyDiscountedWarn": "Some fed orders are discounted by 100% or more, so their net sales are 0 or less. They count like any other order, but the P&L reads a Daily Log row whose net sales are 0 or less at its gross. Check those days against the platform statement.",
  "feed.detail.fullyDiscountedDays": "Days with fully discounted orders (latest first):",
  "feed.detail.dayOrders": "orders: ",
  "feed.detail.fullyDiscountedMore": "More days: ",
  "feed.detail.tokens": "Tokens",
  "feed.detail.tokenCreated": "Created",
  "feed.detail.tokenFirstSeen": "First seen",
  "feed.detail.tokenRevoked": "Revoked",
  "feed.detail.tokenNotSeen": "Not seen yet",
  "feed.detail.tokenActive": "Active",
  "feed.detail.noTokens": "No tokens.",
  "feed.detail.today": "Today by platform",
  "feed.detail.todayEmpty": "No orders today yet.",
  "feed.detail.colLocation": "Location",
  "feed.detail.colPlatform": "Platform code",
  "feed.detail.colChannel": "Channel",
  "feed.detail.colOrders": "Orders",
  "feed.detail.colGross": "Gross",
  "feed.detail.unmappedChannel": "Not mapped",

  // ── Store, location and platform mapping (plan 7.5 steps 6 and 7) ───
  "feed.map.title": "Stores and platforms",
  "feed.map.loadError": "Couldn't load stores and locations.",
  "feed.map.storesEmpty": "No stores seen yet. Stores appear after the first order arrives or a report is imported.",
  "feed.map.colStore": "Store",
  "feed.map.colBrand": "Brand",
  "feed.map.colPos": "POS ref",
  "feed.map.colHeld": "Orders held",
  "feed.map.colLastSeen": "Last seen",
  "feed.map.colLocation": "Netrev location",
  "feed.map.notMapped": "Not mapped (its orders count nowhere)",
  "feed.map.noBrand": "No brand",
  "feed.map.archived": " (archived)",
  "feed.map.multiBrand": "Several brands under one Biz ID: map each brand to its own location (a P&L per brand, more location fees) or to one location's channels (one fee). The owner decides.",
  "feed.map.locationsTitle": "Locations",
  "feed.map.noLocations": "Map a store to a location to set its time zone and platforms.",
  "feed.map.timeZone": "Time zone",
  "feed.map.timeZoneInvalid": "Use an IANA time zone, for example Europe/London.",
  "feed.map.timeZoneRequired": "Set a time zone first.",
  "feed.map.useZone": "Use ",
  "feed.map.cutoff": "Trading day cutoff",
  "feed.map.cutoffHint": "Orders before this time count for the previous day. Empty means midnight.",
  "feed.map.cutoffInvalid": "The cutoff must be before 12:00.",
  "feed.map.zoneLocked": "Locked: a platform at this location is live, so its time zone and cutoff can't change.",
  "feed.map.noPlatforms": "No platform codes seen at this location yet.",
  "feed.map.colCode": "Platform code",
  "feed.map.colSeen": "Orders seen",
  "feed.map.colChannel": "Netrev channel",
  "feed.map.colFeedFrom": "Feed from",
  "feed.map.colNotes": "What it doesn't send",
  "feed.map.notTracked": "Not tracked",
  "feed.map.suggested": "Suggested: ",
  "feed.map.suggestedTag": " (suggested)",
  "feed.map.takenOther": " (fed by another connection)",
  "feed.map.takenCode": " (mapped to another code)",
  "feed.map.inactive": " (inactive)",
  "feed.map.feedFromHint": "The first local day the feed owns: from then on it replaces the typed sales on this channel (the rows are kept for restore). Empty means mapped but not live.",
  "feed.map.feedFromNeedsChannel": "Pick a channel first.",
  "feed.map.liveRemoveWarn": "This route is live. Removing it is refused once it has fed days.",
  "feed.map.laterWarn": "Moving the start later, or clearing it, is refused if the feed has already written days it would give up.",
  "feed.map.channelsNote": "Channels come from the location's Configuration. A channel is never created here: add a missing one in Configuration first.",
  "feed.map.generalNote": "UrbanPiper sends no commission, platform fee, refund, payout, currency or time zone for any platform. Those still come from channel settings and statements.",
  "feed.map.cofundNote": "Discounts on fed days have an unknown funder, so co-funding stays off there: a new Platform contribution entry over fed days is refused, and so is a feed start or a resume that would cover an existing entry. An entry already on fed days still changes their figures today, and the connection shows the Platform contribution on fed days warning: shorten or delete it in the Daily Log.",
  "feed.map.save": "Save mapping",
  "feed.map.changes": "Unsaved changes: ",
  "feed.map.undo": "Undo changes",
  "feed.map.backdateTitle": "Replace typed sales from these dates?",
  "feed.map.backdateBody": "Saving hands these channels to the feed from the dates shown. The sales already typed there are replaced as soon as the feed has orders for those days: at once for orders it already holds, otherwise when its stored orders apply, while the connection is live and approved for billing. The replaced rows are kept for restore, but once the feed writes rows, a start date can't be moved later. Check each date, the year included.",
  "feed.map.backdateConfirm": "Save and replace",
  "feed.map.backdateFrom": " from ",

  // ── What each platform doesn't send (plan 7.3, appendix 7) ──────────
  "feed.note.funderUnknown": "Doesn't say who funded a discount.",
  "feed.note.acceptanceTime": "Its order time is when the restaurant accepted the order, not when it was placed.",
  "feed.note.posCancel": "Doesn't take cancellations made in the POS or Atlas: such an order shows as cancelled in UrbanPiper, so check the statement.",
  "feed.note.jahezCancel": "Never relays its own cancellations.",
  "feed.note.swiggyShare": "Its discount may hold only the restaurant's share: don't add a platform contribution entry for this channel.",
  "feed.note.deliveryFeeDiscount": "Delivery fee discounts come off charges, not food. The formula is still to be decided.",
  "feed.note.zomatoRequest": "A customer cancellation is a request, not a cancellation.",
  "feed.note.ownChannel": "The merchant's own ordering channel: not tracked unless the owner decides otherwise.",
  "feed.note.unknownPlatform": "Unverified platform code: not tracked unless the owner decides otherwise.",

  // ── Typed days: replaced by default, kept by override (plan 7.5 step 8;
  //    owner decision 21 Oct 2026) ─────────────────────────────────────
  "feed.conf.title": "Days already typed",
  "feed.conf.intro": "From each route's feed start, the feed replaces the sales already typed for that channel and day, synced and statement rows included. The replaced rows are kept and can be restored. To keep a day's typed rows instead, tick Keep typed rows and save: the typed rows come back and the feed writes nothing for that day. Untick it to hand the day back to the feed. Only a super admin can keep typed rows.",
  "feed.conf.loadError": "Couldn't load days already typed.",
  "feed.conf.empty": "No typed days in this range: the feed hasn't replaced any.",
  "feed.conf.from": "From",
  "feed.conf.to": "To",
  "feed.conf.show": "Show",
  "feed.conf.rangeInvalid": "Pick a start on or before the end, at most 92 days apart.",
  "feed.conf.colDate": "Day",
  "feed.conf.colLocation": "Location",
  "feed.conf.colChannel": "Channel",
  "feed.conf.colTyped": "Typed",
  "feed.conf.colFeed": "Feed",
  "feed.conf.colChoice": "Which sales count",
  "feed.conf.rows": "Rows: ",
  "feed.conf.gross": "Gross: ",
  "feed.conf.net": "Net: ",
  "feed.conf.orders": "Orders: ",
  "feed.conf.netRaw": "Net after discounts: ",
  "feed.conf.fullyDiscounted": "Fully discounted: ",
  "feed.conf.keepOverride": "Keep typed rows (override)",
  "feed.conf.statusReplaced": "Replaced by the feed",
  "feed.conf.statusKept": "Typed rows kept",
  "feed.conf.statusHeld": "No feed order on this day yet: the typed rows stay, locked, until there is one",
  "feed.conf.replacedCount": "Replaced by the feed: ",
  "feed.conf.heldCount": "No feed order yet: ",
  "feed.conf.keptCount": "Kept typed: ",
  "feed.conf.allKeep": "Keep typed rows on every day shown",
  "feed.conf.save": "Save choices",
  "feed.conf.tooMany": "Save at most 366 days at a time.",

  // ── History import: the Order Transactions Report (contract 5.11;
  //    plan 7.5 item 11; appendix 7 step 11) ───────────────────────────
  "feed.import.title": "History import",
  "feed.import.intro": "Import past orders from UrbanPiper's Order Transactions Report. In Atlas, open All Apps > Central Report > Order Transaction Report, pick the dates and download it as CSV or XLSX. Orders go into the feed's ledger under UrbanPiper's order ID: uploading the same file again changes nothing, a corrected export replaces the earlier upload's figures, and a later file can record cancellations the live feed missed. Imported orders reach the Daily Log only on days inside a route's feed window. Days before a route's feed start stay in the ledger until that start is moved back to cover them in Stores and platforms; the sales typed on those days are then replaced (the typed rows are kept for restore).",
  "feed.import.privacy": "The file is read in this browser. Customer columns (names, phone numbers, addresses, cities, customer IDs, payment transaction IDs) are removed before anything is sent, and the file itself is never uploaded or kept.",
  "feed.import.file": "Report file (CSV or XLSX)",
  "feed.import.fileHint": "Up to 20 MB. Save an old .xls file as CSV or XLSX first.",
  "feed.import.reading": "Reading the file…",
  "feed.import.readError.wrongType": "Choose a CSV or XLSX file.",
  "feed.import.readError.xls": "This is an old Excel .xls file, which can't be read here. Open it, save it as CSV or XLSX, and upload that.",
  "feed.import.readError.tooBig": "This file is over 20 MB. Download a shorter date range and upload it in parts.",
  "feed.import.readError.readFailed": "Couldn't read this file. Download it again and try once more.",
  "feed.import.readError.readerUnavailable": "The XLSX reader couldn't be loaded. Check your connection and try again, or upload the CSV version.",
  "feed.import.readError.xlsxUnreadable": "This XLSX file couldn't be read. Download it again, or upload the CSV version.",
  "feed.import.readError.empty": "This file is empty.",
  "feed.import.readError.noHeader": "This doesn't look like the Order Transactions Report: no header row names an order ID, a Created At time and a store ID. Upload the export as it was downloaded, as CSV (comma separated) or XLSX.",
  "feed.import.readError.noRows": "The report has a header row but no orders.",
  "feed.import.lookedFor": "Columns looked for: ",
  "feed.import.fileLabel": "File: ",
  "feed.import.rowsRead": "Rows read: ",
  "feed.import.rowsValid": "Readable orders: ",
  "feed.import.rowsSkipped": "Left out: ",
  "feed.import.customerRemoved": "Customer columns removed, never uploaded: ",
  "feed.import.unusedColumns": "Other columns not used: ",
  "feed.import.skippedTitle": "Rows left out",
  "feed.import.fileRows": "file rows ",
  "feed.import.reason.missing_order_id": "No order ID",
  "feed.import.reason.missing_store_id": "No store ID",
  "feed.import.reason.invalid_created_at": "No readable order date and time (a date alone, or an XLSX date cell at exactly midnight, isn't enough)",
  "feed.import.reason.duplicate_in_request": "The same order ID again (the first row is used)",
  "feed.import.reason.ref_mismatch": "Already in the ledger as another order (other references or creation time)",
  "feed.import.reason.missing_platform_code": "No platform (the Channel column is empty)",
  "feed.import.reason.missing_subtotal": "No readable Sub-total Amount",
  "feed.import.reason.live_day_not_in_feed": "On a day the live feed covers, but the feed doesn't have this order (it may hold it under another ID)",
  "feed.import.reason.location_not_ready": "The store's location is archived or has no time zone yet",
  "feed.import.reason.malformed": "The row can't be read as an order",
  "feed.import.reason.refused_by_inbox": "The server couldn't store this row (a value is too long)",
  "feed.import.reason.invalid_order_id": "The order ID can't be used (it's too long, or it reads as a date)",
  "feed.import.reason.invalid_store_id": "The store ID can't be used (it's too long, or it reads as a date)",
  "feed.import.reason.invalid_brand_id": "The brand ID can't be used (it's too long, or it reads as a date)",
  "feed.import.reason.invalid_discount": "The Discount isn't a readable amount",
  "feed.import.reason.unreadable_row": "The server couldn't read this row",
  "feed.import.reason.scientific_id": "A spreadsheet app rewrote the ID (for example as 1.23E+18), so its digits are lost. Download the report again and upload it without saving it in Excel.",
  "feed.import.reason.cell_count_mismatch": "The row's cells don't line up with the header (often a comma or line break inside a value that isn't in quotes), so it isn't read. Download the report again and upload it as it was downloaded, or as XLSX.",
  "feed.import.reason.unknown": "Refused for another reason",
  "feed.import.timeZone": "Time zone of the report's times",
  "feed.import.timeZoneRequired": "Pick the time zone the report's times are in.",
  "feed.import.timeZoneInvalid": "Use an IANA time zone, for example Europe/London.",
  "feed.import.timeZoneHint": "UrbanPiper doesn't say which zone Created At uses. It's filled in with the mapped locations' zone when they share one.",
  "feed.import.timeZoneOffsets": "Every time in this file carries its own offset, so the zone is optional.",
  "feed.import.dayFirst": "Dates in this file are day first (04/11/2026 is 4 November)",
  "feed.import.dayFirstHint": "No date in the file settles the order, so check it against an order you know.",
  "feed.import.cancelledNote": "Cancelled orders, uploaded so the ledger records the cancellation but not counted in the totals: ",
  "feed.import.fullyDiscountedCount": "Fully discounted orders: ",
  "feed.import.fullyDiscountedWarn": "These orders are discounted by 100% or more, so their net sales are 0 or less. They count like any other order, but the P&L reads a Daily Log row whose net sales are 0 or less at its gross. Check those days against the platform statement.",
  "feed.import.unmappedNote": "Orders for stores not mapped to a location yet: ",
  "feed.import.newStores": "Stores not seen before: ",
  "feed.import.unmappedHint": "They're stored, and they count once the store is mapped in Stores and platforms. Until then their days follow the time zone above, and they're checked against this connection's earliest feed start.",
  "feed.import.unknownPlatforms": "Platform values that aren't UrbanPiper platform codes: ",
  "feed.import.unknownPlatformsHint": "The server reads common display names as their codes (Uber Eats as ubereats, for example), but this preview doesn't: it shows their orders as unmapped and checks them against this connection's earliest feed start. Check any other value with UrbanPiper before importing.",
  "feed.import.windowTitle": "Days on or after the feed start: ",
  "feed.import.windowRows": "orders on them: ",
  "feed.import.windowBody": "These days are on or after a feed start, so they're left out by default: until UrbanPiper confirms that the report's ID is the live feed's order ID, an order the feed already holds could count twice. The server holds the same line on the days its live feed has covered, from 14 hours before the first live order.",
  "feed.import.windowInclude": "Include days on or after the feed start (true-up)",
  "feed.import.windowIncluded": "Included (a true-up): a row that matches an order the feed already has can update it, a missed cancellation for example. The server refuses a row for an order the feed doesn't have on days its live feed has covered; other rows on these days reach the Daily Log once applied, replacing the sales typed there (kept for restore).",
  "feed.import.daysTitle": "Orders by day",
  "feed.import.daysHint": "Business days as the Daily Log would show them: each order's time in its location's time zone, with the location's trading day cutoff. Days before a route's feed start stay in the feed's ledger until a feed start covers them. Gross is the sum of Sub-total Amount, and net is gross minus Discount. Cancelled orders aren't counted.",
  "feed.import.col.day": "Day",
  "feed.import.col.location": "Location",
  "feed.import.col.platform": "Platform",
  "feed.import.col.orders": "Orders",
  "feed.import.col.gross": "Gross",
  "feed.import.col.net": "Net",
  "feed.import.col.cancelled": "Cancelled",
  "feed.import.col.notes": "Notes",
  "feed.import.col.fileRow": "File row",
  "feed.import.col.reason": "Reason",
  "feed.import.notMapped": "Store not mapped yet",
  "feed.import.noPlatform": "No platform",
  "feed.import.chip.window": "On or after feed start",
  "feed.import.chip.excluded": "Not uploaded",
  "feed.import.chip.fullyDiscounted": "Fully discounted: ",
  "feed.import.tableCapped": "Only the first 400 rows are shown.",
  "feed.import.toUpload": "Orders to upload: ",
  "feed.import.parts": "parts: ",
  "feed.import.nothingToUpload": "Nothing to upload.",
  "feed.import.upload": "Upload orders",
  "feed.import.uploading": "Uploading…",
  "feed.import.progress": "Uploading part ",
  "feed.import.of": " of ",
  "feed.import.failedParts": "Parts uploaded before the error: ",
  "feed.import.rateLimitedHint": "Another history upload is running on the server, which takes one at a time. Wait until it has finished.",
  "feed.import.failedRetry": "The rows already sent are saved, and sending them again changes nothing, so upload the file again to finish.",
  "feed.import.doneTitle": "Upload finished.",
  "feed.import.doneStored": "The orders are stored. The feed applies them shortly, while the connection is approved for billing and not paused: orders on days inside a route's feed window then reach the Daily Log, and the rest stay in the feed's ledger until a feed start covers their days.",
  "feed.import.res.received": "Received: ",
  "feed.import.res.inserted": "New or corrected: ",
  "feed.import.res.unchanged": "Unchanged: ",
  "feed.import.res.rejected": "Refused: ",
  "feed.import.res.storesNew": "New stores: ",
  "feed.import.storesNewHint": "New stores appear unmapped in Stores and platforms. Map them to count their orders.",
  "feed.import.warningsTitle": "Notes from the server",
  "feed.import.warningsMore": "More notes: ",
  "feed.import.warningsShowAll": "Show all notes",
  "feed.import.rejectedTitle": "Rows the server refused",
  "feed.import.another": "Import another file",

  // ── Daily Log sources of typed rows ──────────────────────────────────
  "feed.source.manual": "Typed",
  "feed.source.scrape": "Portal sync",
  "feed.source.statement": "Statement",
};

export const ar = {
  // ── Navigation ───────────────────────────────────────────────────────
  "nav.admin.feed": "التكاملات",
  "feed.accounts.setUp": "إعداد الربط",

  // ── Page chrome ──────────────────────────────────────────────────────
  "feed.title": "التكاملات",
  "feed.desc": "إعداد ربط الطلبات لحسابات التجربة: أنشئ اتصالًا، وسلّم فريق UrbanPiper تفاصيل الـ Webhook، واربط المتاجر والمنصات، وراجع الأيام المُدخلة التي يستبدلها الربط.",
  "feed.refresh": "تحديث",
  "feed.new": "اتصال جديد",
  "feed.retry": "حاول مرة أخرى",
  "feed.close": "إغلاق",
  "feed.cancel": "إلغاء",
  "feed.dismiss": "إخفاء",
  "feed.working": "جارٍ التنفيذ…",
  "feed.saving": "جارٍ الحفظ…",

  // ── Connections list ─────────────────────────────────────────────────
  "feed.list.search": "ابحث باسم الحساب أو معرّف النشاط",
  "feed.list.empty": "لا توجد اتصالات بعد. استخدم «اتصال جديد» أو «إعداد الربط» في صفحة الحسابات لبدء تجربة.",
  "feed.list.noMatch": "لا توجد اتصالات تطابق هذا البحث.",
  "feed.list.loadError": "تعذّر تحميل الاتصالات.",
  "feed.list.unmapped": "غير مربوطة: ",
  "feed.list.liveMonth": "نشطة هذا الشهر: ",
  "feed.list.noEvents": "لا أحداث بعد",
  "feed.list.noWarnings": "لا يوجد",
  "feed.list.pauseReason": "السبب: ",
  "feed.col.account": "الحساب",
  "feed.col.provider": "المزوّد",
  "feed.col.bizId": "معرّف النشاط",
  "feed.col.status": "الحالة",
  "feed.col.approval": "موافقة الفوترة",
  "feed.col.lastEvent": "آخر حدث",
  "feed.col.stores": "المتاجر",
  "feed.col.ordersToday": "طلبات اليوم",
  "feed.col.warnings": "التنبيهات",
  "feed.action.manage": "إدارة",
  "feed.action.hide": "إخفاء",
  "feed.action.pause": "إيقاف مؤقت",
  "feed.action.resume": "استئناف",
  "feed.action.approve": "موافقة",
  "feed.action.revoke": "سحب الموافقة",
  "feed.action.rotate": "تغيير الرمز المميّز",

  // ── Statuses ─────────────────────────────────────────────────────────
  "feed.status.pending": "بانتظار أول طلب",
  "feed.status.live": "نشط",
  "feed.status.paused": "متوقف مؤقتًا",
  "feed.status.disconnected": "مفصول",
  "feed.status.unknown": "غير معروف",
  "feed.approval.on": "تمت الموافقة",
  "feed.approval.off": "بلا موافقة",

  // ── Warning codes (contract 4.2) ─────────────────────────────────────
  "feed.warn.unmapped_store": "متجر غير مربوط",
  "feed.warn.unmapped_platform": "منصة غير مربوطة",
  "feed.warn.time_zone_missing": "موقع بلا منطقة زمنية",
  "feed.warn.not_approved": "بلا موافقة على الفوترة",
  "feed.warn.access_lost": "فقد الحساب الوصول المدفوع",
  "feed.warn.apply_errors": "تعذّر تطبيق بعض الأحداث",
  "feed.warn.fully_discounted_orders": "طلبات بخصم كامل",
  "feed.warn.no_status_updates": "لا تصل تحديثات الحالة (هل الـ Webhook الثاني مفقود؟)",
  "feed.warn.open_orders": "طلبات لم تكتمل بعد",
  "feed.warn.quiet_store": "متجر بلا طلبات خلال ساعات العمل",
  "feed.warn.cofund_on_fed_channel": "مساهمة منصة على أيام مغذّاة",
  "feed.warn.unknown": "تنبيه آخر",

  // ── Error codes (contract sections 3, 5 and 7) ──────────────────────
  "feed.err.invalid_request": "رُفض الطلب لأنه غير صالح. تحقّق من القيم وحاول مرة أخرى.",
  "feed.err.unauthorized": "انتهت جلستك. سجّل الدخول مرة أخرى.",
  "feed.err.forbidden": "ليست لديك صلاحية للقيام بذلك.",
  "feed.err.feed_not_enabled": "الربط متوقف لهذا المزوّد على هذا الخادم.",
  "feed.err.not_paid": "لا يملك هذا الحساب وصولًا مدفوعًا، لذا لا يمكن إعداد الربط أو تغييره.",
  "feed.err.not_found": "غير موجود. ربما أُزيل، أو أن الربط متوقف.",
  "feed.err.connection_disconnected": "هذا الاتصال مفصول.",
  "feed.err.payload_too_large": "البيانات في الطلب الواحد كثيرة جدًا.",
  "feed.err.rate_limited": "طلبات كثيرة جدًا. انتظر قليلًا ثم حاول مرة أخرى.",
  "feed.err.internal_error": "واجهت خدمة التكامل خطأ. حاول مرة أخرى، وأبلغ عنه إذا تكرّر.",
  "feed.err.unavailable": "تعذّر على خدمة التكامل الوصول إلى قاعدة البيانات أو خدمة تسجيل الدخول الآن. حاول مرة أخرى بعد قليل.",
  "feed.err.unsupported_provider": "هذا المزوّد غير مدعوم بعد.",
  "feed.err.invalid_biz_id": "يجب أن يتكوّن معرّف النشاط من 1 إلى 20 رقمًا.",
  "feed.err.consent_required": "سجّل موافقة التاجر وإصدار الشروط أولًا.",
  "feed.err.connection_exists": "لدى هذا الحساب اتصال قائم بالفعل لمعرّف النشاط هذا.",
  "feed.err.biz_id_claimed": "معرّف النشاط هذا مرتبط بالفعل بحساب آخر في Netrev.",
  "feed.err.unknown_store": "أحد المتاجر في هذا الحفظ لا يتبع هذا الاتصال. حدّث الصفحة وحاول مرة أخرى.",
  "feed.err.workspace_not_in_account": "هذا الموقع ليس ضمن حساب هذا الاتصال.",
  "feed.err.workspace_archived": "هذا الموقع مؤرشف. اختر موقعًا نشطًا.",
  "feed.err.channel_not_in_workspace": "هذه القناة لا تتبع الموقع المختار.",
  "feed.err.invalid_time_zone": "منطقة زمنية غير معروفة. استخدم اسمًا من IANA مثل Europe/London.",
  "feed.err.invalid_cutoff": "يجب أن يكون وقت إقفال يوم العمل قبل 12:00.",
  "feed.err.invalid_platform_code": "رمز منصة غير صالح.",
  "feed.err.time_zone_required": "حدّد المنطقة الزمنية للموقع قبل اختيار تاريخ بدء الربط.",
  "feed.err.channel_already_fed": "هذه القناة مربوطة بالفعل باتصال آخر.",
  "feed.err.channel_has_other_code": "يربط هذا الاتصال هذه القناة بالفعل برمز منصة آخر.",
  "feed.err.channel_has_fed_days": "لهذا المسار أيام مغذّاة بالفعل، لذا لا يمكن إزالته.",
  "feed.err.feed_from_after_fed_days": "لا يمكن نقل تاريخ البدء إلى ما بعد أيام كتبها الربط بالفعل.",
  "feed.err.workspace_is_fed": "لا يمكن تغيير المنطقة الزمنية ووقت الإقفال ما دامت إحدى منصات هذا الموقع نشطة.",
  "feed.err.invalid_range": "نطاق التاريخ غير صالح.",
  "feed.err.range_too_long": "اختر نطاقًا لا يزيد على 92 يومًا.",
  "feed.err.too_many_choices": "احفظ 366 يومًا على الأكثر في كل مرة.",
  "feed.err.day_not_fed": "أحد هذه الأيام لم يعد ضمن نافذة الربط. حدّث الصفحة وحاول مرة أخرى.",
  "feed.err.too_many_rows": "عدد الصفوف في دفعة الرفع الواحدة كبير جدًا.",
  "feed.err.invalid_status": "لا تسمح حالة الاتصال بذلك الآن. حدّث الصفحة لرؤية حالته الحالية.",
  "feed.err.invalid_month": "شهر غير صالح.",
  "feed.err.not_approved": "لم تتم الموافقة على فوترة هذا الاتصال بعد، لذا لا يمكنه استقبال استيراد. بعد أن يوافق عليه المسؤول الكبير، ارفع الملف مرة أخرى.",
  "feed.err.connection_paused": "هذا الاتصال متوقف مؤقتًا، لذا لا يمكنه استقبال استيراد. استأنفه، ثم ارفع الملف مرة أخرى.",
  "feed.err.unreadable_report": "تعذّر على خدمة التكامل قراءة هذا التقرير. نزّله مرة أخرى وارفعه كما نُزّل.",
  "feed.err.preview_stale": "تغيّر التقرير أو طلبات الربط أثناء التحقق منه. ارفع الملف مرة أخرى.",
  "feed.err.too_many_days": "يغطي هذا التقرير أيامًا كثيرة جدًا لرفع واحد. نزّل نطاقات تواريخ أقصر وارفعها واحدًا تلو الآخر.",
  "feed.err.channel_has_cofunding": "يغطي إدخال مساهمة منصة أيامًا سيسلّمها هذا التغيير إلى الربط. يبقى التمويل المشترك متوقفًا في الأيام المغذّاة، لذا قصّر ذلك الإدخال أو احذفه في السجل اليومي أولًا.",
  "feed.err.auth_unavailable": "تعذّر الوصول إلى خدمة تسجيل الدخول الآن. حاول مرة أخرى بعد قليل.",
  "feed.err.db_unavailable": "تعذّر على خدمة التكامل الوصول إلى قاعدة البيانات الآن. حاول مرة أخرى بعد قليل.",
  "feed.err.not_signed_in": "لم تسجّل الدخول. سجّل الدخول مرة أخرى.",
  "feed.err.network_error": "تعذّر الوصول إلى خدمة التكامل. تحقّق من اتصالك وحاول مرة أخرى.",
  "feed.err.timeout": "استغرقت خدمة التكامل وقتًا طويلًا للرد. حاول مرة أخرى.",
  "feed.err.bad_response": "أرسلت خدمة التكامل ردًا لا تفهمه هذه الشاشة.",
  "feed.err.unexpected_response": "أرسلت خدمة التكامل ردًا غير متوقع.",
  "feed.err.unknown": "حدث خطأ ما.",
  "feed.err.codePrefix": "الرمز: ",

  // ── Create connection dialog (plan 7.5 steps 2 to 4, shape b) ───────
  "feed.create.title": "اتصال جديد",
  "feed.create.intro": "تفعيل التجربة: يعرض Netrev عنوان Webhook ورمزًا مميّزًا مرة واحدة، ويضيف فريق UrbanPiper كلا الـ Webhook في Atlas. لا يحتفظ Netrev بأي بيانات اعتماد لـ UrbanPiper.",
  "feed.create.account": "معرّف حساب Netrev",
  "feed.create.accountHint": "المعرّف الكامل للحساب. يملؤه زر «إعداد الربط» في صفحة الحسابات.",
  "feed.create.accountFrom": "من قائمة الحسابات: ",
  "feed.create.accountInvalid": "أدخل المعرّف الكامل للحساب (UUID).",
  "feed.create.provider": "المزوّد",
  "feed.create.bizId": "معرّف النشاط في UrbanPiper (Biz ID)",
  "feed.create.bizIdHint": "معرّف النشاط في حساب التاجر على Atlas. أرقام فقط.",
  "feed.create.bizIdInvalid": "يجب أن يتكوّن معرّف النشاط من 1 إلى 20 رقمًا.",
  "feed.create.checklistTitle": "قبل المتابعة",
  "feed.create.checkPos": "يستقبل التاجر طلبات UrbanPiper بالفعل في نظام نقطة بيع أو في لوحة طلبات UrbanPiper. يكتفي Netrev بالاستماع: لا يمكنه قبول الطلبات أو رفضها أو إلغاؤها.",
  "feed.create.consentTitle": "موافقة التاجر",
  "feed.create.consentBody": "سيستقبل Netrev الطلبات والخصومات والإلغاءات ومعرّفات الطلبات لدى المتاجر والمنصات. ولا يخزّن أبدًا أسماء العملاء أو أرقام هواتفهم أو عناوينهم أو بيانات السائقين أو الملاحظات النصية. وفي الأيام التي يغطيها الربط، تحلّ مبيعات الربط محلّ المبيعات المُدخلة، وتُحفظ الصفوف المُدخلة ويمكن استعادتها.",
  "feed.create.consentCheck": "وافق التاجر على هذه الشروط. أسجّل هذه الموافقة نيابةً عنه (مثل شروط وُقّعت خارج المنصة)، ويسجّلني Netrev بصفتي من سجّلها.",
  "feed.create.terms": "إصدار الشروط التي وافق عليها التاجر",
  "feed.create.termsHint": "من 1 إلى 40 حرفًا، مثل مرجع شروط معالجة البيانات الموقّعة.",
  "feed.create.termsInvalid": "أدخل إصدار الشروط (من 1 إلى 40 حرفًا).",
  "feed.create.submit": "إنشاء وعرض الرمز المميّز",
  "feed.create.shownTitle": "تم إنشاء الاتصال",
  "feed.create.reconnectHint": "إعادة الربط: يُعاد استخدام الاتصال المفصول لهذا الحساب ومعرّف النشاط ويبدأ من جديد. تُلغى رموزه المميّزة القديمة، وتُسجَّل موافقة التاجر مرة أخرى، ويحتاج مجددًا إلى موافقة المسؤول الكبير على الفوترة.",

  // ── The one-time token (create and rotate) ──────────────────────────
  "feed.token.warning": "انسخ الرمز المميّز الآن، فلن يُعرض مرة أخرى. إذا فُقد، غيّر الرمز المميّز.",
  "feed.token.webhookUrl": "عنوان الـ Webhook",
  "feed.token.headerName": "اسم الترويسة",
  "feed.token.token": "الرمز المميّز",
  "feed.token.copy": "نسخ",
  "feed.token.copied": "تم النسخ",
  "feed.token.copyFallback": "النسخ غير متاح هنا. النص محدّد: اضغط Ctrl+C (أو Cmd+C).",
  "feed.token.stepsTitle": "خطوات فريق UrbanPiper في Atlas",
  "feed.token.step1": "افتح Settings > Webhooks > Add Webhook.",
  "feed.token.step2": "أضف Webhook للحدث Order placed باستخدام العنوان أعلاه.",
  "feed.token.step3": "أضف Webhook ثانيًا للحدث Order status update بالعنوان نفسه.",
  "feed.token.step4": "في كلا الـ Webhook، فعّل إعادة المحاولة واختر الدقائق.",
  "feed.token.step5Prefix": "في كلا الـ Webhook، أضف ترويسة باسم ",
  "feed.token.step5Suffix": " وقيمتها الرمز المميّز. احتفظ بالشرطات: يُتجاهل اسم الترويسة الذي يحتوي على شرطات سفلية.",
  "feed.token.share": "أرسل الرمز المميّز إلى فريق UrbanPiper عبر قناة آمنة، وليس عبر بريد إلكتروني عادي أبدًا.",
  "feed.token.next": "ينتظر الاتصال أول طلب. وافق على فوترته واربط متاجره ومنصاته قبل أن تُحتسب طلباته. وبدءًا من تاريخ بدء الربط لقناة ما، يستبدل الربط المبيعات المُدخلة في تلك القناة، وتُحفظ الصفوف المُدخلة للاستعادة.",
  "feed.token.done": "نسخته. تم",

  // ── Rotate token ─────────────────────────────────────────────────────
  "feed.rotate.title": "تغيير الرمز المميّز؟",
  "feed.rotate.body": "يُعرض رمز مميّز جديد مرة واحدة. يبقى الرمز الحالي صالحًا حتى يصل أول حدث بالرمز الجديد، ثم يُلغى. ويُلغى الآن أي رمز جديد سابق لم يُستخدم قط.",
  "feed.rotate.confirm": "تغيير الرمز المميّز",
  "feed.rotate.revokeLabel": "إلغاء الرمز المميّز المستخدم الآن أيضًا (لرمز مميّز تسرّب)",
  "feed.rotate.revokeHint": "تتوقف كل الرموز المميّزة الحالية عن العمل فورًا. وتُرفض الأحداث التي ترسلها UrbanPiper بالرمز القديم (وتعيد UrbanPiper محاولة إرسالها) إلى أن يحمل كلا الـ Webhook في Atlas الرمز الجديد، لذا غيّرهما فورًا.",
  "feed.rotate.shownTitle": "الرمز المميّز الجديد",
  "feed.rotate.stepPrefix": "في Atlas، غيّر قيمة الترويسة ",
  "feed.rotate.stepSuffix": " في كلا الـ Webhook الخاصين بـ Netrev إلى الرمز المميّز الجديد.",

  // ── Pause, resume, approval ──────────────────────────────────────────
  "feed.pause.title": "إيقاف هذا الاتصال مؤقتًا؟",
  "feed.pause.body": "تستمر الأحداث بالوصول وتُخزّن، لكن لا يُطبَّق شيء على السجل اليومي. يُعاد فتح الإدخال اليدوي من الغد، وتبقى الأيام المغذّاة، بما فيها اليوم، كما هي. وعند الاستئناف، يستبدل الربط المبيعات المُدخلة خلال الإيقاف في القنوات المغذّاة (مع حفظ الصفوف المُدخلة للاستعادة).",
  "feed.pause.reason": "السبب (اختياري)",
  "feed.pause.confirm": "إيقاف مؤقت",
  "feed.resume.title": "استئناف هذا الاتصال؟",
  "feed.resume.body": "تُطبَّق الأحداث المحتجزة بالترتيب. ويستبدل الربط المبيعات المُدخلة أثناء الإيقاف في القنوات المغذّاة (مع حفظ الصفوف المُدخلة للاستعادة)، باستثناء الأيام التي أُبقي فيها على الصفوف المُدخلة.",
  "feed.resume.confirm": "استئناف",
  "feed.approve.title": "الموافقة على فوترة هذا الاتصال؟",
  "feed.approve.body": "تفوتر UrbanPiper شركة Netrev عن كل مطعم نشط. بعد الموافقة، تُطبَّق الأحداث على السجل اليومي للمتاجر والمنصات المربوطة.",
  "feed.approve.confirm": "موافقة",
  "feed.revoke.title": "سحب موافقة الفوترة؟",
  "feed.revoke.body": "تستمر الأحداث بالوصول وتُخزّن، لكنها لا تُطبَّق حتى تتم الموافقة على الاتصال مجددًا.",
  "feed.revoke.confirm": "سحب الموافقة",

  // ── Notices ──────────────────────────────────────────────────────────
  "feed.notice.created": "تم إنشاء الاتصال، وهو بانتظار أول طلب.",
  "feed.notice.rotated": "تم تغيير الرمز المميّز.",
  "feed.notice.paused": "تم إيقاف الاتصال مؤقتًا.",
  "feed.notice.resumed": "تم استئناف الاتصال.",
  "feed.notice.approved": "تمت الموافقة على الفوترة.",
  "feed.notice.revoked": "تم سحب موافقة الفوترة.",
  "feed.notice.mappingSaved": "تم حفظ الربط.",
  "feed.notice.choicesSaved": "تم حفظ القرارات.",
  "feed.notice.replacedDays": "أيام مُدخلة استبدلها الربط (مع حفظ صفوفها للاستعادة): ",
  "feed.notice.replaceHeld": "أيام أُعيدت إلى الربط وليس فيها طلب بعد (تبقى صفوفها المُدخلة مقفلة إلى أن يصل طلب): ",
  "feed.notice.imported": "تم رفع الطلبات السابقة، وسيطبّقها الربط قريبًا.",

  // ── Connection detail ────────────────────────────────────────────────
  "feed.detail.loadError": "تعذّر تحميل هذا الاتصال.",
  "feed.detail.status": "الحالة",
  "feed.detail.approval": "موافقة الفوترة",
  "feed.detail.consent": "الموافقة",
  "feed.detail.consentNone": "غير مسجّلة",
  "feed.detail.activation": "التفعيل",
  "feed.detail.activationAtlas": "يضيف فريق UrbanPiper الـ Webhook في Atlas",
  "feed.detail.firstEvent": "أول حدث",
  "feed.detail.lastEvent": "آخر حدث",
  "feed.detail.created": "تاريخ الإنشاء",
  "feed.detail.paused": "الإيقاف المؤقت",
  "feed.detail.openOrders": "طلبات لم تكتمل بعد",
  "feed.detail.unprocessed": "أحداث لم تُعالج بعد",
  "feed.detail.ordersToday": "طلبات اليوم",
  "feed.detail.fullyDiscounted": "طلبات بخصم كامل",
  "feed.detail.fullyDiscountedCount": "طلبات بخصم كامل: ",
  "feed.detail.fullyDiscountedWarn": "بعض الطلبات المغذّاة عليها خصم بنسبة 100% أو أكثر، لذا صافي مبيعاتها 0 أو أقل. وهي تُحتسب مثل أي طلب آخر، لكن حساب الأرباح والخسائر يقرأ صف السجل اليومي الذي صافي مبيعاته 0 أو أقل بقيمة إجماليه. راجع تلك الأيام مقابل كشف المنصة.",
  "feed.detail.fullyDiscountedDays": "أيام فيها طلبات بخصم كامل (الأحدث أولًا):",
  "feed.detail.dayOrders": "الطلبات: ",
  "feed.detail.fullyDiscountedMore": "أيام أخرى: ",
  "feed.detail.tokens": "الرموز المميّزة",
  "feed.detail.tokenCreated": "تاريخ الإنشاء",
  "feed.detail.tokenFirstSeen": "أول استخدام",
  "feed.detail.tokenRevoked": "تاريخ الإلغاء",
  "feed.detail.tokenNotSeen": "لم يُستخدم بعد",
  "feed.detail.tokenActive": "صالح",
  "feed.detail.noTokens": "لا توجد رموز مميّزة.",
  "feed.detail.today": "اليوم حسب المنصة",
  "feed.detail.todayEmpty": "لا طلبات اليوم بعد.",
  "feed.detail.colLocation": "الموقع",
  "feed.detail.colPlatform": "رمز المنصة",
  "feed.detail.colChannel": "القناة",
  "feed.detail.colOrders": "الطلبات",
  "feed.detail.colGross": "الإجمالي",
  "feed.detail.unmappedChannel": "غير مربوطة",

  // ── Store, location and platform mapping (plan 7.5 steps 6 and 7) ───
  "feed.map.title": "المتاجر والمنصات",
  "feed.map.loadError": "تعذّر تحميل المتاجر والمواقع.",
  "feed.map.storesEmpty": "لم يظهر أي متجر بعد. تظهر المتاجر بعد وصول أول طلب أو استيراد تقرير.",
  "feed.map.colStore": "المتجر",
  "feed.map.colBrand": "العلامة التجارية",
  "feed.map.colPos": "مرجع نقطة البيع",
  "feed.map.colHeld": "طلبات محتجزة",
  "feed.map.colLastSeen": "آخر ظهور",
  "feed.map.colLocation": "موقع Netrev",
  "feed.map.notMapped": "غير مربوط (لا تُحتسب طلباته في أي مكان)",
  "feed.map.noBrand": "بلا علامة تجارية",
  "feed.map.archived": " (مؤرشف)",
  "feed.map.multiBrand": "عدة علامات تجارية تحت معرّف نشاط واحد: اربط كل علامة بموقع خاص بها (حساب أرباح وخسائر لكل علامة ورسوم مواقع أكثر) أو بقنوات موقع واحد (رسم واحد). القرار للمالك.",
  "feed.map.locationsTitle": "المواقع",
  "feed.map.noLocations": "اربط متجرًا بموقع لتحديد منطقته الزمنية ومنصاته.",
  "feed.map.timeZone": "المنطقة الزمنية",
  "feed.map.timeZoneInvalid": "استخدم منطقة زمنية من IANA، مثل Europe/London.",
  "feed.map.timeZoneRequired": "حدّد المنطقة الزمنية أولًا.",
  "feed.map.useZone": "استخدم ",
  "feed.map.cutoff": "وقت إقفال يوم العمل",
  "feed.map.cutoffHint": "تُحتسب الطلبات قبل هذا الوقت لليوم السابق. تركه فارغًا يعني منتصف الليل.",
  "feed.map.cutoffInvalid": "يجب أن يكون وقت الإقفال قبل 12:00.",
  "feed.map.zoneLocked": "مقفل: إحدى منصات هذا الموقع نشطة، لذا لا يمكن تغيير منطقته الزمنية ووقت إقفاله.",
  "feed.map.noPlatforms": "لم يظهر أي رمز منصة في هذا الموقع بعد.",
  "feed.map.colCode": "رمز المنصة",
  "feed.map.colSeen": "الطلبات الواردة",
  "feed.map.colChannel": "قناة Netrev",
  "feed.map.colFeedFrom": "بدء الربط",
  "feed.map.colNotes": "ما لا ترسله",
  "feed.map.notTracked": "غير متتبَّعة",
  "feed.map.suggested": "مقترح: ",
  "feed.map.suggestedTag": " (مقترحة)",
  "feed.map.takenOther": " (مربوطة باتصال آخر)",
  "feed.map.takenCode": " (مربوطة برمز آخر)",
  "feed.map.inactive": " (غير نشطة)",
  "feed.map.feedFromHint": "أول يوم محلي يتولاه الربط: ومنذ ذلك اليوم يستبدل المبيعات المُدخلة في هذه القناة (مع حفظ الصفوف للاستعادة). تركه فارغًا يعني أنها مربوطة لكن غير نشطة.",
  "feed.map.feedFromNeedsChannel": "اختر قناة أولًا.",
  "feed.map.liveRemoveWarn": "هذا المسار نشط. تُرفض إزالته إذا كانت له أيام مغذّاة.",
  "feed.map.laterWarn": "يُرفض تأخير تاريخ البدء أو مسحه إذا كان الربط قد كتب بالفعل أيامًا سيتخلى عنها.",
  "feed.map.channelsNote": "تأتي القنوات من إعدادات الموقع. لا تُنشأ أي قناة هنا: أضف القناة الناقصة في الإعدادات أولًا.",
  "feed.map.generalNote": "لا ترسل UrbanPiper أي عمولة أو رسوم منصة أو استرداد أو دفعات أو عملة أو منطقة زمنية لأي منصة. وتبقى هذه من إعدادات القنوات والكشوف.",
  "feed.map.cofundNote": "مموّل الخصومات في الأيام المغذّاة غير معروف، لذا يبقى التمويل المشترك متوقفًا فيها: يُرفض أي إدخال جديد لمساهمة المنصة على أيام مغذّاة، ويُرفض كذلك تاريخ بدء ربط أو استئناف يغطي إدخالًا قائمًا. وما زال الإدخال الموجود بالفعل على أيام مغذّاة يغيّر أرقامها اليوم، ويُظهر الاتصال تنبيه «مساهمة منصة على أيام مغذّاة»: قصّر ذلك الإدخال أو احذفه في السجل اليومي.",
  "feed.map.save": "حفظ الربط",
  "feed.map.changes": "تغييرات غير محفوظة: ",
  "feed.map.undo": "التراجع عن التغييرات",
  "feed.map.backdateTitle": "استبدال المبيعات المُدخلة بدءًا من هذه التواريخ؟",
  "feed.map.backdateBody": "يؤدي الحفظ إلى تسليم هذه القنوات إلى الربط بدءًا من التواريخ المعروضة. وتُستبدل المبيعات المُدخلة فيها بمجرد أن تتوفر لدى الربط طلبات لتلك الأيام: فورًا للطلبات التي يحتفظ بها بالفعل، وإلا فعند تطبيق طلباته المخزّنة، ما دام الاتصال نشطًا وموافقًا على فوترته. وتُحفظ الصفوف المستبدلة للاستعادة، لكن بعد أن يكتب الربط صفوفًا لا يمكن تأخير تاريخ البدء. تحقّق من كل تاريخ، بما في ذلك السنة.",
  "feed.map.backdateConfirm": "حفظ واستبدال",
  "feed.map.backdateFrom": " بدءًا من ",

  // ── What each platform doesn't send (plan 7.3, appendix 7) ──────────
  "feed.note.funderUnknown": "لا تذكر من موّل الخصم.",
  "feed.note.acceptanceTime": "وقت الطلب لديها هو وقت قبول المطعم له، لا وقت تقديمه.",
  "feed.note.posCancel": "لا تستقبل الإلغاءات التي تتم في نقطة البيع أو Atlas: يظهر هذا الطلب ملغى في UrbanPiper، لذا راجع الكشف.",
  "feed.note.jahezCancel": "لا تنقل إلغاءاتها الخاصة أبدًا.",
  "feed.note.swiggyShare": "قد يتضمن الخصم حصة المطعم فقط: لا تُضف إدخال مساهمة منصة لهذه القناة.",
  "feed.note.deliveryFeeDiscount": "تُخصم خصومات رسوم التوصيل من الرسوم لا من الطعام. ولم تُحدَّد المعادلة بعد.",
  "feed.note.zomatoRequest": "إلغاء العميل طلبٌ للإلغاء، وليس إلغاءً.",
  "feed.note.ownChannel": "قناة الطلب الخاصة بالتاجر: غير متتبَّعة ما لم يقرر المالك خلاف ذلك.",
  "feed.note.unknownPlatform": "رمز منصة غير متحقَّق منه: غير متتبَّع ما لم يقرر المالك خلاف ذلك.",

  // ── Typed days: replaced by default, kept by override (plan 7.5 step 8;
  //    owner decision 21 Oct 2026) ─────────────────────────────────────
  "feed.conf.title": "الأيام المُدخلة مسبقًا",
  "feed.conf.intro": "بدءًا من تاريخ بدء الربط لكل مسار، يستبدل الربط المبيعات المُدخلة مسبقًا لتلك القناة في ذلك اليوم، بما فيها صفوف المزامنة والكشوف. وتُحفظ الصفوف المستبدلة ويمكن استعادتها. للإبقاء على الصفوف المُدخلة ليوم ما بدلًا من ذلك، حدّد «الإبقاء على الصفوف المُدخلة» ثم احفظ: تعود الصفوف المُدخلة ولا يكتب الربط شيئًا لذلك اليوم. ألغِ التحديد لإعادة اليوم إلى الربط. الإبقاء على الصفوف المُدخلة صلاحية للمسؤول الكبير وحده.",
  "feed.conf.loadError": "تعذّر تحميل الأيام المُدخلة مسبقًا.",
  "feed.conf.empty": "لا توجد أيام مُدخلة في هذا النطاق: لم يستبدل الربط أي يوم.",
  "feed.conf.from": "من",
  "feed.conf.to": "إلى",
  "feed.conf.show": "عرض",
  "feed.conf.rangeInvalid": "اختر بداية في تاريخ النهاية أو قبله، بفارق لا يزيد على 92 يومًا.",
  "feed.conf.colDate": "اليوم",
  "feed.conf.colLocation": "الموقع",
  "feed.conf.colChannel": "القناة",
  "feed.conf.colTyped": "المُدخل",
  "feed.conf.colFeed": "الربط",
  "feed.conf.colChoice": "المبيعات المعتمدة",
  "feed.conf.rows": "الصفوف: ",
  "feed.conf.gross": "الإجمالي: ",
  "feed.conf.net": "الصافي: ",
  "feed.conf.orders": "الطلبات: ",
  "feed.conf.netRaw": "الصافي بعد الخصومات: ",
  "feed.conf.fullyDiscounted": "بخصم كامل: ",
  "feed.conf.keepOverride": "الإبقاء على الصفوف المُدخلة (استثناء)",
  "feed.conf.statusReplaced": "استبدلها الربط",
  "feed.conf.statusKept": "أُبقي على الصفوف المُدخلة",
  "feed.conf.statusHeld": "لا يوجد طلب من الربط في هذا اليوم بعد: تبقى الصفوف المُدخلة مقفلة إلى أن يصل طلب",
  "feed.conf.replacedCount": "استبدلها الربط: ",
  "feed.conf.heldCount": "لا يوجد طلب من الربط بعد: ",
  "feed.conf.keptCount": "مُبقاة كما أُدخلت: ",
  "feed.conf.allKeep": "الإبقاء على الصفوف المُدخلة في كل الأيام المعروضة",
  "feed.conf.save": "حفظ القرارات",
  "feed.conf.tooMany": "احفظ 366 يومًا على الأكثر في كل مرة.",

  // ── History import: the Order Transactions Report (contract 5.11;
  //    plan 7.5 item 11; appendix 7 step 11) ───────────────────────────
  "feed.import.title": "استيراد الطلبات السابقة",
  "feed.import.intro": "استورد الطلبات السابقة من تقرير معاملات الطلبات في UrbanPiper. في Atlas، افتح All Apps > Central Report > Order Transaction Report، واختر التواريخ، ونزّل التقرير بصيغة CSV أو XLSX. تدخل الطلبات دفتر طلبات الربط بمعرّف الطلب في UrbanPiper: فرفع الملف نفسه مرة أخرى لا يغيّر شيئًا، والتصدير المصحَّح يحلّ محلّ أرقام الرفع السابق، ويمكن لملف لاحق أن يسجّل الإلغاءات التي فاتت الربط المباشر. ولا تصل الطلبات المستوردة إلى السجل اليومي إلا في الأيام الواقعة ضمن نافذة ربط أحد المسارات. وتبقى الأيام السابقة لتاريخ بدء ربط المسار في دفتر الطلبات إلى أن يُقدَّم ذلك التاريخ ليغطيها في «المتاجر والمنصات»، وعندها تُستبدل المبيعات المُدخلة في تلك الأيام (وتُحفظ الصفوف المُدخلة للاستعادة).",
  "feed.import.privacy": "يُقرأ الملف في هذا المتصفح. تُحذف أعمدة العملاء (الأسماء وأرقام الهواتف والعناوين والمدن ومعرّفات العملاء ومعرّفات معاملات الدفع) قبل إرسال أي شيء، ولا يُرفع الملف نفسه ولا يُحتفظ به.",
  "feed.import.file": "ملف التقرير (CSV أو XLSX)",
  "feed.import.fileHint": "حتى 20 ميغابايت. احفظ ملف xls القديم بصيغة CSV أو XLSX أولًا.",
  "feed.import.reading": "جارٍ قراءة الملف…",
  "feed.import.readError.wrongType": "اختر ملف CSV أو XLSX.",
  "feed.import.readError.xls": "هذا ملف Excel قديم بصيغة xls ولا يمكن قراءته هنا. افتحه واحفظه بصيغة CSV أو XLSX، ثم ارفع الملف الجديد.",
  "feed.import.readError.tooBig": "حجم هذا الملف أكبر من 20 ميغابايت. نزّل نطاق تواريخ أقصر وارفعه على أجزاء.",
  "feed.import.readError.readFailed": "تعذّرت قراءة هذا الملف. نزّله مرة أخرى وحاول من جديد.",
  "feed.import.readError.readerUnavailable": "تعذّر تحميل قارئ ملفات XLSX. تحقّق من اتصالك وحاول مرة أخرى، أو ارفع نسخة CSV.",
  "feed.import.readError.xlsxUnreadable": "تعذّرت قراءة ملف XLSX هذا. نزّله مرة أخرى، أو ارفع نسخة CSV.",
  "feed.import.readError.empty": "هذا الملف فارغ.",
  "feed.import.readError.noHeader": "لا يبدو هذا تقرير معاملات الطلبات: لا يوجد صف عناوين يذكر معرّف الطلب ووقت الإنشاء (Created At) ومعرّف المتجر. ارفع الملف كما نُزّل، بصيغة CSV (مفصولة بفواصل) أو XLSX.",
  "feed.import.readError.noRows": "في التقرير صف عناوين لكن لا توجد فيه طلبات.",
  "feed.import.lookedFor": "الأعمدة المطلوبة: ",
  "feed.import.fileLabel": "الملف: ",
  "feed.import.rowsRead": "الصفوف المقروءة: ",
  "feed.import.rowsValid": "الطلبات المقروءة: ",
  "feed.import.rowsSkipped": "المستبعدة: ",
  "feed.import.customerRemoved": "أعمدة العملاء المحذوفة (لا تُرفع أبدًا): ",
  "feed.import.unusedColumns": "أعمدة أخرى غير مستخدمة: ",
  "feed.import.skippedTitle": "الصفوف المستبعدة",
  "feed.import.fileRows": "صفوف الملف ",
  "feed.import.reason.missing_order_id": "لا يوجد معرّف طلب",
  "feed.import.reason.missing_store_id": "لا يوجد معرّف متجر",
  "feed.import.reason.invalid_created_at": "لا يوجد تاريخ ووقت مقروءان للطلب (لا يكفي تاريخ وحده، ولا خلية تاريخ في ملف XLSX عند منتصف الليل تمامًا)",
  "feed.import.reason.duplicate_in_request": "معرّف الطلب نفسه مرة أخرى (يُعتمد الصف الأول)",
  "feed.import.reason.ref_mismatch": "موجود في دفتر الطلبات بوصفه طلبًا آخر (مراجع أو وقت إنشاء مختلفان)",
  "feed.import.reason.missing_platform_code": "لا توجد منصة (عمود Channel فارغ)",
  "feed.import.reason.missing_subtotal": "لا يوجد Sub-total Amount مقروء",
  "feed.import.reason.live_day_not_in_feed": "في يوم يغطيه الربط المباشر، لكن هذا الطلب غير موجود في الربط (قد يحتفظ به بمعرّف آخر)",
  "feed.import.reason.location_not_ready": "موقع المتجر مؤرشف أو بلا منطقة زمنية بعد",
  "feed.import.reason.malformed": "لا يمكن قراءة الصف بوصفه طلبًا",
  "feed.import.reason.refused_by_inbox": "تعذّر على الخادم تخزين هذا الصف (إحدى القيم طويلة جدًا)",
  "feed.import.reason.invalid_order_id": "لا يمكن استخدام معرّف الطلب (طويل جدًا، أو يُقرأ بوصفه تاريخًا)",
  "feed.import.reason.invalid_store_id": "لا يمكن استخدام معرّف المتجر (طويل جدًا، أو يُقرأ بوصفه تاريخًا)",
  "feed.import.reason.invalid_brand_id": "لا يمكن استخدام معرّف العلامة التجارية (طويل جدًا، أو يُقرأ بوصفه تاريخًا)",
  "feed.import.reason.invalid_discount": "قيمة Discount ليست مبلغًا مقروءًا",
  "feed.import.reason.unreadable_row": "تعذّر على الخادم قراءة هذا الصف",
  "feed.import.reason.scientific_id": "غيّر برنامج جداول البيانات المعرّف (مثلًا إلى 1.23E+18)، فضاعت أرقامه. نزّل التقرير مرة أخرى وارفعه دون حفظه في Excel.",
  "feed.import.reason.cell_count_mismatch": "خلايا هذا الصف لا تتطابق مع صف العناوين (غالبًا بسبب فاصلة أو سطر جديد داخل قيمة غير محاطة بعلامات اقتباس)، لذا لا يُقرأ. نزّل التقرير مرة أخرى وارفعه كما نُزّل، أو بصيغة XLSX.",
  "feed.import.reason.unknown": "رُفض لسبب آخر",
  "feed.import.timeZone": "المنطقة الزمنية لأوقات التقرير",
  "feed.import.timeZoneRequired": "اختر المنطقة الزمنية التي سُجّلت بها أوقات التقرير.",
  "feed.import.timeZoneInvalid": "استخدم منطقة زمنية من IANA، مثل Europe/London.",
  "feed.import.timeZoneHint": "لا تذكر UrbanPiper المنطقة الزمنية التي يستخدمها وقت الإنشاء (Created At). وتُملأ بمنطقة المواقع المربوطة إن كانت مشتركة بينها.",
  "feed.import.timeZoneOffsets": "كل وقت في هذا الملف يحمل فرق توقيته الخاص، لذا تحديد المنطقة اختياري.",
  "feed.import.dayFirst": "التواريخ في هذا الملف تبدأ باليوم (04/11/2026 تعني 4 نوفمبر)",
  "feed.import.dayFirstHint": "لا يوجد في الملف تاريخ يحسم الترتيب، لذا تحقّق منه مقابل طلب تعرفه.",
  "feed.import.cancelledNote": "طلبات ملغاة، تُرفع ليسجّل دفتر الطلبات إلغاءها لكنها لا تُحتسب في الإجماليات: ",
  "feed.import.fullyDiscountedCount": "طلبات بخصم كامل: ",
  "feed.import.fullyDiscountedWarn": "على هذه الطلبات خصم بنسبة 100% أو أكثر، لذا صافي مبيعاتها 0 أو أقل. وهي تُحتسب مثل أي طلب آخر، لكن حساب الأرباح والخسائر يقرأ صف السجل اليومي الذي صافي مبيعاته 0 أو أقل بقيمة إجماليه. راجع تلك الأيام مقابل كشف المنصة.",
  "feed.import.unmappedNote": "طلبات لمتاجر لم تُربط بموقع بعد: ",
  "feed.import.newStores": "متاجر لم تظهر من قبل: ",
  "feed.import.unmappedHint": "تُحفظ هذه الطلبات، وتُحتسب بمجرد ربط المتجر في «المتاجر والمنصات». وإلى ذلك الحين تتبع أيامها المنطقة الزمنية أعلاه، وتُقارن بأبكر تاريخ بدء ربط لهذا الاتصال.",
  "feed.import.unknownPlatforms": "قيم منصات ليست رموز منصات في UrbanPiper: ",
  "feed.import.unknownPlatformsHint": "يقرأ الخادم أسماء العرض الشائعة بوصفها رموزها (مثلًا Uber Eats بوصفها ubereats)، لكن هذه المعاينة لا تفعل ذلك: فهي تعرض طلباتها غير مربوطة وتقارنها بأبكر تاريخ بدء ربط لهذا الاتصال. راجع أي قيمة أخرى مع UrbanPiper قبل الاستيراد.",
  "feed.import.windowTitle": "أيام في تاريخ بدء الربط أو بعده: ",
  "feed.import.windowRows": "الطلبات فيها: ",
  "feed.import.windowBody": "تقع هذه الأيام في تاريخ بدء ربط أو بعده، لذا تُستبعد افتراضيًا: إلى أن تؤكد UrbanPiper أن معرّف التقرير هو معرّف الطلب في الربط المباشر، قد يُحتسب طلب يحتفظ به الربط بالفعل مرتين. ويطبّق الخادم القاعدة نفسها على الأيام التي غطاها ربطه المباشر، بدءًا من 14 ساعة قبل أول طلب مباشر.",
  "feed.import.windowInclude": "تضمين الأيام في تاريخ بدء الربط أو بعده (تسوية)",
  "feed.import.windowIncluded": "مُضمَّنة (تسوية): يمكن للصف المطابق لطلب موجود بالفعل في الربط أن يحدّثه، كإلغاء فات تسجيله مثلًا. ويرفض الخادم الصف الخاص بطلب غير موجود في الربط في الأيام التي غطاها ربطه المباشر، أما الصفوف الأخرى في هذه الأيام فتصل إلى السجل اليومي بعد تطبيقها، وتحلّ محلّ المبيعات المُدخلة فيها (مع حفظها للاستعادة).",
  "feed.import.daysTitle": "الطلبات حسب اليوم",
  "feed.import.daysHint": "أيام العمل كما سيعرضها السجل اليومي: وقت كل طلب بالمنطقة الزمنية لموقعه، مع وقت إقفال يوم العمل للموقع. وتبقى الأيام السابقة لتاريخ بدء ربط المسار في دفتر طلبات الربط إلى أن يغطيها تاريخ بدء ربط. الإجمالي هو مجموع Sub-total Amount، والصافي هو الإجمالي ناقص Discount. ولا تُحتسب الطلبات الملغاة.",
  "feed.import.col.day": "اليوم",
  "feed.import.col.location": "الموقع",
  "feed.import.col.platform": "المنصة",
  "feed.import.col.orders": "الطلبات",
  "feed.import.col.gross": "الإجمالي",
  "feed.import.col.net": "الصافي",
  "feed.import.col.cancelled": "الملغاة",
  "feed.import.col.notes": "ملاحظات",
  "feed.import.col.fileRow": "صف الملف",
  "feed.import.col.reason": "السبب",
  "feed.import.notMapped": "المتجر غير مربوط بعد",
  "feed.import.noPlatform": "بلا منصة",
  "feed.import.chip.window": "في تاريخ بدء الربط أو بعده",
  "feed.import.chip.excluded": "لن تُرفع",
  "feed.import.chip.fullyDiscounted": "بخصم كامل: ",
  "feed.import.tableCapped": "تُعرض أول 400 صف فقط.",
  "feed.import.toUpload": "الطلبات التي ستُرفع: ",
  "feed.import.parts": "الدفعات: ",
  "feed.import.nothingToUpload": "لا يوجد ما يُرفع.",
  "feed.import.upload": "رفع الطلبات",
  "feed.import.uploading": "جارٍ الرفع…",
  "feed.import.progress": "جارٍ رفع الدفعة ",
  "feed.import.of": " من ",
  "feed.import.failedParts": "الدفعات المرفوعة قبل الخطأ: ",
  "feed.import.rateLimitedHint": "يجري رفع آخر للطلبات السابقة على الخادم، وهو يعالج رفعًا واحدًا في كل مرة. انتظر حتى ينتهي.",
  "feed.import.failedRetry": "الصفوف المرسلة محفوظة، وإرسالها مرة أخرى لا يغيّر شيئًا، لذا ارفع الملف مرة أخرى لإكمال الاستيراد.",
  "feed.import.doneTitle": "اكتمل الرفع.",
  "feed.import.doneStored": "خُزّنت الطلبات، وسيطبّقها الربط قريبًا ما دام الاتصال موافقًا على فوترته وغير متوقف مؤقتًا: فتصل الطلبات الواقعة في أيام ضمن نافذة ربط أحد المسارات إلى السجل اليومي، وتبقى البقية في دفتر طلبات الربط إلى أن يغطي أيامها تاريخ بدء ربط.",
  "feed.import.res.received": "المستلمة: ",
  "feed.import.res.inserted": "الجديدة أو المصحَّحة: ",
  "feed.import.res.unchanged": "دون تغيير: ",
  "feed.import.res.rejected": "المرفوضة: ",
  "feed.import.res.storesNew": "متاجر جديدة: ",
  "feed.import.storesNewHint": "تظهر المتاجر الجديدة غير مربوطة في «المتاجر والمنصات». اربطها لتُحتسب طلباتها.",
  "feed.import.warningsTitle": "ملاحظات من الخادم (بالإنجليزية)",
  "feed.import.warningsMore": "ملاحظات أخرى: ",
  "feed.import.warningsShowAll": "عرض كل الملاحظات",
  "feed.import.rejectedTitle": "الصفوف التي رفضها الخادم",
  "feed.import.another": "استيراد ملف آخر",

  // ── Daily Log sources of typed rows ──────────────────────────────────
  "feed.source.manual": "مُدخل يدويًا",
  "feed.source.scrape": "مزامنة البوابة",
  "feed.source.statement": "كشف",
};
